import React, { useContext } from 'react';
import styled, { css, useTheme } from 'styled-components';
import Button from '../../dashboard/components/common/base/Button';
import { StyledCardSection } from '../../dashboard/components/common/base/Card';
import Link from '../../dashboard/components/common/base/Link';
import Text from '../../dashboard/components/common/base/Text';
import Tooltip from '../../dashboard/components/common/base/Tooltip';
import Dropdown from '../../dashboard/components/common/Dropdown';
import { Div } from '../../dashboard/components/common/helpers/StyledUtils';
import useLocalStorage from '../../dashboard/components/hooks/useLocalStorage';
import { IconName } from '../../dashboard/components/common/base/Icon';
import LINKS from '../../dashboard/configs/links';
import { ConsoleGlobalContext } from './context/ConsoleGlobalContext';
import { appendTracking } from './helpers';

const cards: { title: string; description: string; asset: string; link: string }[] = [
  {
    title: 'Receive every request safely',
    description:
      'Control your event stream with rate limits, customizable retries, and event pausing',
    asset: '/img/discover-safely.svg',
    link: appendTracking(LINKS.product_docs.connections),
  },
  {
    title: 'Centralize events in one platform',
    description:
      'Determine where your events go and tweak their content with Fan Out, Filters, and Transformation',
    asset: '/img/discover-manage.svg',
    link: appendTracking(LINKS.product_docs.connection_rules),
  },
  {
    title: 'Fix problems before impact',
    description:
      'Resolve problems in record time with Issue Triggers, Issues, manual & bulk retries',
    asset: '/img/discover-problems.svg',
    link: appendTracking(LINKS.product_docs.issues),
  },
  {
    title: 'Observability for peace of mind',
    description:
      'From high-level trends to event tracing and inspecting, the Hookdeck Event Gateway has you covered',
    asset: '/img/discover-observability.svg',
    link: appendTracking(LINKS.product_docs.metrics),
  },
];

const StyledImg = styled.img(
  ({ theme }) => css`
    width: 100%;
    border-radius: ${theme.radius.normal};
    overflow: hidden;
    background-color: transparent;
  `,
);

const StyledProgressDot = styled.span<{ active: boolean }>(
  ({ theme, active }) => css`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin: ${theme.spacing(1)};
    background-color: ${active
      ? theme.colors.on.neutral.primary
      : theme.colors.on.neutral.disabled};
  `,
);

const DiscoverCards: React.FC = () => {
  const [card_index, setCardIndex] = useLocalStorage('pref:discover-card:index', 0);
  const card = cards[card_index];
  const theme = useTheme();
  const { user } = useContext(ConsoleGlobalContext);
  const button_props = user.is_guest
    ? {
        href: `${VITE_APP_URL}/signup?ref=hookdeck-console`,
        icon: 'upgrade' as IconName,
        text: 'Sign up for the Event Gateway',
        tooltip: 'Support your full SDLC',
      }
    : {
        href: `${VITE_APP_URL}`,
        icon: 'leave' as IconName,
        text: 'Try the Event Gateway',
        tooltip: 'Support your full SDLC',
      };

  return (
    <Dropdown
      placement="bottom-end"
      renderToggle={(open, toggle) => (
        <Button
          outline
          badge={
            card_index <= cards.length - 2
              ? { label: String(cards.length - card_index) }
              : undefined
          }
          icon="info"
          onClick={() => toggle(!open)}>
          Event Gateway
        </Button>
      )}>
      <StyledCardSection p={4} w={{ px: 416 }}>
        <Div m={{ b: 4 }}>
          <Text muted size="s" m={{ b: 1 }}>
            Hookdeck Event Gateway
          </Text>
          <Text bold size="l" m={{ b: 2 }}>
            {card.title}
          </Text>
          <Text muted as="p" m={{ b: 3 }}>
            {card.description}
          </Text>
          <Link href={card.link} target="_blank" icon="arrow_forward">
            Learn more
          </Link>
        </Div>
        <StyledImg
          src={`${VITE_CONSOLE_URL}${card.asset}`}
          style={{ filter: theme.mode === 'dark' ? 'invert(0.9) grayscale(1)' : undefined }}
        />
        <Div flex={{ justify: 'center' }} m={{ t: 4 }}>
          {cards.map((_, index) => (
            <StyledProgressDot key={index} active={index === card_index} />
          ))}
        </Div>
      </StyledCardSection>
      <StyledCardSection p={4} flex={{ justify: 'space-between' }}>
        <Div flex={{ align: 'center' }}>
          <Tooltip tooltip={button_props.tooltip} side="bottom">
            <Button m={{ r: 3 }} primary as="a" href={button_props.href} icon={button_props.icon}>
              {button_props.text}
            </Button>
          </Tooltip>
        </Div>
        <Div flex={{ align: 'center' }}>
          <Button
            ghost
            icon="chevron_left"
            disabled={card_index === 0}
            onClick={() => setCardIndex(card_index - 1)}
          />
          <Button
            m={{ l: 2 }}
            ghost
            icon="chevron_right"
            disabled={card_index === cards.length - 1}
            onClick={() => setCardIndex(card_index + 1)}
          />
        </Div>
      </StyledCardSection>
    </Dropdown>
  );
};

export default DiscoverCards;
